import React, { useCallback, useEffect, useState } from "react";
import "../assets/styles/components/header.css";
import { scrollToSectionId } from "../utils/scrollToSection";

const SECTIONS = [
    { id: "home", label: "Home" },
    { id: "education", label: "Education" },
    { id: "skills", label: "Skills" },
    { id: "experience", label: "Experience" },
    { id: "contact", label: "Contact" },
] as const;

function Header() {
    const [activeId, setActiveId] = useState<string>("home");
    const [menuOpen, setMenuOpen] = useState(false);

    const updateActive = useCallback(() => {
        const mid = window.scrollY + window.innerHeight * 0.35;
        let current: string = SECTIONS[0].id;
        for (const { id } of SECTIONS) {
            const el = document.getElementById(id);
            if (!el) continue;
            const top = el.offsetTop;
            if (top <= mid) current = id;
        }
        setActiveId(current);
    }, []);

    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                updateActive();
                ticking = false;
            });
        };
        updateActive();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [updateActive]);

    useEffect(() => {
        document.body.classList.toggle("nav-open", menuOpen);
        return () => document.body.classList.remove("nav-open");
    }, [menuOpen]);

    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMenuOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [menuOpen]);

    const go = (id: string) => {
        scrollToSectionId(id);
        setMenuOpen(false);
    };

    return (
        <header className="site-header" role="banner">
            <div
                className={`site-header__scrim${menuOpen ? " is-visible" : ""}`}
                aria-hidden="true"
                onClick={() => setMenuOpen(false)}
            />
            <div className="site-header__container">
                <div className="site-header__bar">
                    <a
                        className="site-header__brand"
                        href="#home"
                        onClick={(e) => {
                            e.preventDefault();
                            go("home");
                        }}
                    >
                        <span className="site-header__brand-mark" aria-hidden="true" />
                        Anton Korolov
                    </a>
                    <button
                        type="button"
                        className={`site-header__toggle${menuOpen ? " is-open" : ""}`}
                        aria-expanded={menuOpen}
                        aria-controls="site-nav"
                        onClick={() => setMenuOpen((o) => !o)}
                    >
                        <span className="site-header__burger" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                        </span>
                        <span className="visually-hidden">{menuOpen ? "Close menu" : "Open menu"}</span>
                    </button>
                </div>
            </div>
            <nav id="site-nav" className={`site-header__nav${menuOpen ? " is-open" : ""}`} aria-label="Primary">
                    <ul className="site-header__list">
                        {SECTIONS.map(({ id, label }) => (
                            <li key={id}>
                                <a
                                    href={`#${id}`}
                                    className={`site-header__link${activeId === id ? " is-active" : ""}`}
                                    aria-current={activeId === id ? "true" : undefined}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        go(id);
                                    }}
                                >
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
        </header>
    );
}

export default Header;
